import React, { useState } from "react";
import { useStyles } from "./FileManagerStyles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  makeStyles,
  IconButton,
  Tooltip
} from "@material-ui/core";
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Folder as FolderIcon,
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Visibility as ViewIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import useVPSMonitoring from "../hooks/useVPSMonitoring";
import { useTimezone } from "../../../../hooks/useTimezone";
import { VPS_FILE_CLEANUP_ENABLED, VPS_FILE_CLEANUP_OFF_MSG } from "../../../../config/features";


const formatSize = (sizeMB) => {
  if (!sizeMB || sizeMB === 0) return "0 B";
  if (sizeMB < 1) {
    return `${(sizeMB * 1024).toFixed(2)} KB`;
  } else if (sizeMB < 1024) {
    return `${sizeMB.toFixed(2)} MB`;
  } else {
    return `${(sizeMB / 1024).toFixed(2)} GB`;
  }
};

const FileManager = ({ loading, error, fileAnalysis, onRefresh }) => {
  const classes = useStyles();
  const { formatDateTime } = useTimezone();
  const { deleteFiles, listFiles } = useVPSMonitoring();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileTypeToDelete, setFileTypeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Estados para modal de detalle
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  
  // Estados para confirmación de eliminación individual
  const [deleteSingleDialogOpen, setDeleteSingleDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  
  // Estados para visor de archivos
  const [viewerModalOpen, setViewerModalOpen] = useState(false);
  const [fileToView, setFileToView] = useState(null);

  const handleDeleteClick = (fileType) => {
    setFileTypeToDelete(fileType);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileTypeToDelete) return;

    try {
      setDeleting(true);
      await deleteFiles([fileTypeToDelete], []);
      toast.success(`Archivos tipo ${fileTypeToDelete} eliminados correctamente`);
      setDeleteDialogOpen(false);
      setFileTypeToDelete(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      toast.error(`Error eliminando archivos: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFileTypeToDelete(null);
  };

  // Abrir modal de detalle al hacer click en cantidad
  const handleQuantityClick = async (fileType) => {
    setSelectedFileType(fileType);
    setDetailModalOpen(true);
    setLoadingFiles(true);
    setFileList([]);
    
    try {
      const result = await listFiles(fileType, 1000); // Obtener hasta 1000 archivos
      if (result && result.data && result.data.files) {
        setFileList(result.data.files);
      } else if (result && result.files) {
        // Compatibilidad con formato anterior
        setFileList(result.files);
      } else {
        setFileList([]);
      }
    } catch (err) {
      toast.error(`Error cargando archivos ${fileType}: ${err.message}`);
      setFileList([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Cerrar modal de detalle
  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedFileType(null);
    setFileList([]);
  };

  // Abrir diálogo de confirmación para eliminar archivo individual
  const handleDeleteSingleFileClick = (file) => {
    setFileToDelete(file);
    setDeleteSingleDialogOpen(true);
  };

  // Confirmar eliminación de archivo individual
  const handleDeleteSingleFileConfirm = async () => {
    if (!fileToDelete) return;

    try {
      setDeletingFile(fileToDelete.path);
      await deleteFiles([], [fileToDelete.path]);
      toast.success("Archivo eliminado correctamente");
      
      // Actualizar lista de archivos
      const updatedList = fileList.filter(f => f.path !== fileToDelete.path);
      setFileList(updatedList);
      
      // Refrescar análisis general
      if (onRefresh) {
        onRefresh();
      }
      
      setDeleteSingleDialogOpen(false);
      setFileToDelete(null);
    } catch (err) {
      toast.error(`Error eliminando archivo: ${err.message}`);
    } finally {
      setDeletingFile(null);
    }
  };

  // Cancelar eliminación de archivo individual
  const handleDeleteSingleFileCancel = () => {
    setDeleteSingleDialogOpen(false);
    setFileToDelete(null);
  };

  // Obtener URL del archivo para visualización
  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    
    const backendUrl = window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(':3000', ':8080');
    
    // El path viene como ruta absoluta del servidor, necesitamos extraer la parte relativa a /public
    let relativePath = filePath.replace(/\\/g, '/');
    
    // Buscar la parte después de 'public' en el path
    const publicIndex = relativePath.toLowerCase().indexOf('public');
    if (publicIndex !== -1) {
      relativePath = relativePath.substring(publicIndex + 6); // +6 para 'public'
      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath;
      }
    } else {
      // Si no tiene 'public', asumimos que es relativo desde la raíz
      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath;
      }
    }
    
    return `${backendUrl}/public${relativePath}`;
  };

  // Abrir visor de archivo
  const handleViewFile = (file) => {
    setFileToView(file);
    setViewerModalOpen(true);
  };

  // Cerrar visor de archivo
  const handleCloseViewer = () => {
    setViewerModalOpen(false);
    setFileToView(null);
  };

  // Obtener tipo de archivo para determinar el visor
  const getFileType = (fileName) => {
    if (!fileName) return 'unknown';
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['mp4', 'webm', 'ogg'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audio';
    if (ext === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    return 'unknown';
  };

  // Renderizar miniatura según tipo de archivo
  const renderThumbnail = (file) => {
    const fileType = getFileType(file.name);
    const fileUrl = getFileUrl(file.path);
    
    if (fileType === 'image' && fileUrl) {
      return (
        <img 
          src={fileUrl} 
          alt={file.name}
          style={{ 
            width: 60, 
            height: 60, 
            objectFit: 'cover', 
            borderRadius: 4,
            cursor: 'pointer'
          }}
          onClick={() => handleViewFile(file)}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    }
    
    if (fileType === 'video' && fileUrl) {
      return (
        <Box
          style={{
            width: 60,
            height: 60,
            backgroundColor: '#1a1a1a',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
          }}
          onClick={() => handleViewFile(file)}
        >
          <video
            src={fileUrl}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
            muted
            preload="metadata"
            onLoadedMetadata={(e) => {
              // Intentar capturar frame en el segundo 1
              try {
                e.target.currentTime = 1;
              } catch (err) {
                // Ignorar errores
              }
            }}
            onSeeked={(e) => {
              // Capturar frame cuando se haya movido al segundo 1
              try {
                const canvas = document.createElement('canvas');
                const video = e.target;
                canvas.width = video.videoWidth || 60;
                canvas.height = video.videoHeight || 60;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const thumbnail = canvas.toDataURL();
                
                // Reemplazar video con imagen thumbnail
                const img = document.createElement('img');
                img.src = thumbnail;
                img.style.width = '100%';
                img.style.height = '100%';
                img.style.objectFit = 'cover';
                img.style.borderRadius = '4px';
                e.target.parentElement.replaceChild(img, e.target);
                
                // Agregar icono de play encima
                const playIcon = document.createElement('div');
                playIcon.innerHTML = '▶';
                playIcon.style.position = 'absolute';
                playIcon.style.color = 'white';
                playIcon.style.fontSize = '20px';
                playIcon.style.textShadow = '0 0 4px rgba(0,0,0,0.8)';
                e.target.parentElement.appendChild(playIcon);
              } catch (err) {
                // Si falla, mostrar solo el icono de play
                e.target.style.display = 'none';
              }
            }}
          />
          <PlayIcon style={{ position: 'absolute', color: 'white', fontSize: 24, zIndex: 1 }} />
        </Box>
      );
    }
    
    // Icono genérico para otros tipos
    return (
      <Box
        style={{
          width: 60,
          height: 60,
          backgroundColor: '#2a2a2a',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
        onClick={() => handleViewFile(file)}
      >
        <FolderIcon style={{ color: '#666', fontSize: 32 }} />
      </Box>
    );
  };

  if (loading && fileAnalysis.length === 0) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && fileAnalysis.length === 0) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  // Calcular total de peso de todos los archivos
  const totalSizeMB = fileAnalysis.reduce((sum, file) => sum + (file.totalSizeMB || 0), 0);
  const formatTotalSize = (sizeMB) => {
    if (sizeMB < 1024) {
      return `${sizeMB.toFixed(2)} MB`;
    }
    return `${(sizeMB / 1024).toFixed(2)} GB`;
  };

  return (
    <>
      <Box className={classes.header}>
        <Typography style={{ fontSize: 16, fontWeight: 700 }}>
          <FolderIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
          Archivos subidos por los usuarios
          {fileAnalysis.length > 0 && (
            <Typography component="span" variant="body2" style={{ marginLeft: 12, color: "textSecondary" }}>
              (Total: {formatTotalSize(totalSizeMB)})
            </Typography>
          )}
        </Typography>
        <Tooltip title="Actualizar análisis">
          <span>
            <IconButton onClick={onRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo de Archivo</TableCell>
              <TableCell align="right">Cantidad</TableCell>
              <TableCell align="right">Tamaño Total</TableCell>
              <TableCell align="right">Última Análisis</TableCell>
              <TableCell align="center" className={classes.actionCell}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fileAnalysis.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No hay archivos analizados aún
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              fileAnalysis.map((file) => (
                <TableRow key={file.fileType}>
                  <TableCell>
                    <Chip label={file.fileType.toUpperCase()} size="small" />
                  </TableCell>
                  <TableCell 
                    align="right"
                    style={{ cursor: "pointer", color: "#1976d2", textDecoration: "underline" }}
                    onClick={() => handleQuantityClick(file.fileType)}
                  >
                    {file.fileCount.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" className={classes.sizeCell}>
                    {formatSize(file.totalSizeMB)}
                  </TableCell>
                  <TableCell align="right">
                    {file.lastAnalyzed
                      ? formatDateTime(file.lastAnalyzed)
                      : "N/A"}
                  </TableCell>
                  <TableCell align="center" className={classes.actionCell}>
                    <Tooltip title={VPS_FILE_CLEANUP_ENABLED ? `Eliminar todos los archivos ${file.fileType}` : VPS_FILE_CLEANUP_OFF_MSG}>
                      <span>
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleDeleteClick(file.fileType)}
                          disabled={!VPS_FILE_CLEANUP_ENABLED || deleting || file.fileCount === 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de confirmación */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar todos los archivos de tipo{" "}
            <strong>{fileTypeToDelete?.toUpperCase()}</strong>?
            <br />
            <br />
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="secondary"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de detalle de archivos */}
      <Dialog 
        open={detailModalOpen} 
        onClose={handleDetailModalClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Archivos tipo {selectedFileType?.toUpperCase()}
            </Typography>
            <IconButton onClick={handleDetailModalClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingFiles ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : fileList.length === 0 ? (
            <Typography variant="body2" color="textSecondary" align="center">
              No hay archivos de este tipo
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Total: {fileList.length} archivo(s) - {formatSize(fileList.reduce((sum, f) => sum + (f.sizeMB || 0), 0))}
              </Typography>
              <TableContainer component={Paper} style={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 80 }}>Vista Previa</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell align="right">Tamaño</TableCell>
                      <TableCell align="right">Modificado</TableCell>
                      <TableCell align="center" style={{ width: 150 }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fileList.map((file, index) => (
                      <TableRow key={file.path || index}>
                        <TableCell>
                          {renderThumbnail(file)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={file.path}>
                            <Typography variant="body2" style={{ maxWidth: 500, wordBreak: 'break-word' }}>
                              {/* Mostrar ruta relativa desde /public */}
                              {(() => {
                                const fullPath = file.path || '';
                                const publicIndex = fullPath.toLowerCase().indexOf('public');
                                if (publicIndex !== -1) {
                                  const relativePath = fullPath.substring(publicIndex + 6).replace(/\\/g, '/');
                                  return relativePath.startsWith('/') ? relativePath : '/' + relativePath;
                                }
                                return file.name || fullPath.split(/[/\\]/).pop();
                              })()}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {formatSize(file.sizeMB || 0)}
                        </TableCell>
                        <TableCell align="right">
                          {file.modified
                            ? formatDateTime(file.modified)
                            : "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="Ver/Reproducir archivo">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewFile(file)}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={VPS_FILE_CLEANUP_ENABLED ? "Eliminar archivo" : VPS_FILE_CLEANUP_OFF_MSG}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleDeleteSingleFileClick(file)}
                                  disabled={!VPS_FILE_CLEANUP_ENABLED || deletingFile === file.path}
                                >
                                  {deletingFile === file.path ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailModalClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para eliminar archivo individual */}
      <Dialog open={deleteSingleDialogOpen} onClose={handleDeleteSingleFileCancel}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar este archivo?
            <br />
            <br />
            <strong>{fileToDelete?.name || fileToDelete?.path?.split(/[/\\]/).pop()}</strong>
            <br />
            <br />
            Tamaño: {fileToDelete ? formatSize(fileToDelete.sizeMB || 0) : "N/A"}
            <br />
            <br />
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteSingleFileCancel} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteSingleFileConfirm}
            color="secondary"
            variant="contained"
            disabled={deletingFile === fileToDelete?.path}
          >
            {deletingFile === fileToDelete?.path ? <CircularProgress size={20} /> : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal visor de archivos */}
      <Dialog 
        open={viewerModalOpen} 
        onClose={handleCloseViewer}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                {fileToView?.name || "Vista de archivo"}
              </Typography>
              {fileToView?.path && (
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                  {(() => {
                    const fullPath = fileToView.path || '';
                    const publicIndex = fullPath.toLowerCase().indexOf('public');
                    if (publicIndex !== -1) {
                      const relativePath = fullPath.substring(publicIndex + 6).replace(/\\/g, '/');
                      return relativePath.startsWith('/') ? relativePath : '/' + relativePath;
                    }
                    return fullPath;
                  })()}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseViewer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {fileToView && (() => {
            const fileType = getFileType(fileToView.name);
            const fileUrl = getFileUrl(fileToView.path);
            
            if (!fileUrl) {
              return (
                <Typography variant="body2" color="error" align="center">
                  No se pudo cargar el archivo
                </Typography>
              );
            }

            switch (fileType) {
              case 'video':
                return (
                  <Box style={{ 
                    width: '100%', 
                    height: '70vh',
                    minHeight: '400px',
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    backgroundColor: '#000',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <video 
                      controls 
                      autoPlay
                      playsInline
                      preload="auto"
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'contain',
                        backgroundColor: '#000',
                        minHeight: '300px'
                      }}
                      onLoadedMetadata={(e) => {
                        // Si las dimensiones son 0, mostrar mensaje informativo
                        if (e.target.videoWidth === 0 && e.target.videoHeight === 0) {
                          const infoMsg = document.getElementById('video-info-message');
                          if (infoMsg) {
                            infoMsg.style.display = 'block';
                            setTimeout(() => {
                              infoMsg.style.display = 'none';
                            }, 5000);
                          }
                        }
                      }}
                      crossOrigin="anonymous"
                    >
                      <source src={fileUrl} type={`video/${fileToView.name.split('.').pop()}`} />
                      <source src={fileUrl} type="video/mp4" />
                      <source src={fileUrl} />
                      Tu navegador no soporta la reproducción de video.
                    </video>
                    {/* Mensaje informativo si el video no tiene dimensiones */}
                    <Box 
                      id="video-info-message"
                      style={{
                        position: 'absolute',
                        bottom: 60,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: 4,
                        fontSize: '12px',
                        display: 'none'
                      }}
                    >
                      Este archivo puede ser solo audio o tener un codec de video no soportado. El audio se reproducirá normalmente.
                    </Box>
                  </Box>
                );
              
              case 'audio':
                return (
                  <Box style={{ width: '100%', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="h6" style={{ marginBottom: 16 }}>
                      {fileToView.name}
                    </Typography>
                    <audio controls style={{ width: '100%', maxWidth: 600 }}>
                      <source src={fileUrl} type={`audio/${fileToView.name.split('.').pop()}`} />
                      Tu navegador no soporta la reproducción de audio.
                    </audio>
                  </Box>
                );
              
              case 'pdf':
                return (
                  <Box style={{ width: '100%', height: '70vh' }}>
                    <iframe
                      src={fileUrl}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                      title={fileToView.name}
                    />
                  </Box>
                );
              
              case 'image':
                return (
                  <Box style={{ width: '100%', display: 'flex', justifyContent: 'center', maxHeight: '70vh' }}>
                    <img 
                      src={fileUrl} 
                      alt={fileToView.name}
                      style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                    />
                  </Box>
                );
              
              default:
                return (
                  <Box style={{ padding: 24, textAlign: 'center' }}>
                    <Typography variant="body1" style={{ marginBottom: 16 }}>
                      Tipo de archivo no soportado para vista previa
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary"
                      href={fileUrl}
                      target="_blank"
                      download
                    >
                      Descargar archivo
                    </Button>
                  </Box>
                );
            }
          })()}
        </DialogContent>
        <DialogActions>
          {fileToView && (
            <Button 
              href={getFileUrl(fileToView.path)} 
              target="_blank" 
              download={fileToView.name}
              color="primary"
            >
              Descargar
            </Button>
          )}
          <Button onClick={handleCloseViewer} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileManager;
